import { useMemo } from "react";
import dayjs from "dayjs";
import { Box, Button, Typography } from "@mui/material";
import { ProjectImage } from "../types";
import { imageUrl } from "../utils";
import { CheckBox, CheckCircleOutline, SelectAll, TaskAlt } from "@mui/icons-material";

interface GalleryGridProps {
  images: ProjectImage[];
  selectMode: boolean;
  selected: number[];
  onSelect: (id: number | number[]) => void;
  searchTerm?: string;
}

interface Bucket {
  title: string;
  images: ProjectImage[];
}

export function GalleryGrid({ images, onSelect, selected, selectMode }: GalleryGridProps) {
  const filteredImages = images;
  const buckets: Bucket[] = useMemo(() => {
    if (!filteredImages.length) return [];
    const sorted = [...filteredImages].sort((a, b) => {
      const da = a.madeAt ?? a.createdAt;
      const db = b.madeAt ?? b.createdAt;
      return db > da ? 1 : -1;
    });

    const list: Bucket[] = [];
    for (const image of sorted) {
      const title = dayjs
        .utc(image.madeAt ?? image.createdAt, "YYYY-MM-DD HH:mm:ss")
        .local()
        .calendar(undefined, {
          sameDay: "[Heute]",
          lastDay: "[Gestern]",
          nextDay: "[]",
          lastWeek: "[letzten] dddd",
          nextWeek: "[]",
          sameElse: "[am] DD.MM.YYYY",
        });

      if (!list.length || list[list.length - 1].title !== title) {
        list.push({ title, images: [] });
      }
      list[list.length - 1].images.push(image);
    }
    return list;
  }, [filteredImages]);

  if (!buckets.length) {
    return (
      <Typography variant="body1" color="text.secondary">
        Keine Bilder vorhanden.
      </Typography>
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      {buckets.map((bucket) => (
        <Box key={bucket.title} display="flex" flexDirection="column" gap={1}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6">{bucket.title}</Typography>
            {selectMode ? (
              <Button onClick={() => onSelect(bucket.images.map((x) => x.id))} size="small" variant={"outlined"} startIcon={<SelectAll />}>
                Alle
              </Button>
            ) : null}
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
              gap: 1,
            }}
          >
            {bucket.images.map((img) => (
              <Box
                key={img.id}
                sx={{
                  cursor: "pointer",
                  borderRadius: 1,
                  overflow: "hidden",
                  display: "block",
                  width: "100%",
                  position: "relative",
                  aspectRatio: "1 / 1",
                  bgcolor: "grey.100",
                  transition: "opacity 0.2s ease",
                  "&:hover": { opacity: 0.85 },
                }}
                onClick={() => onSelect(img.id)}
              >
                <Box
                  component="img"
                  src={imageUrl(img.thumbnail)}
                  alt={img.description || img.originalFilename}
                  title={img.description || img.originalFilename}
                  loading="lazy"
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                  }}
                />
                {selectMode && selected.includes(img.id) ? (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      backgroundColor: "rgba(255,255,255,0.6)",
                      bottom: 0,
                      display: "flex",
                      fontSize: "40px",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CheckCircleOutline fontSize="inherit" />
                  </Box>
                ) : null}
              </Box>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
}
